/**
 * Budget Manager - v13 Section 6.10
 *
 * Manages LLM usage tracking and budget enforcement.
 *
 * PERSISTENCE:
 * - When a StorageBackend is provided, budget data persists across page refreshes
 * - Uses NAMESPACES.LLM_BUDGET namespace for storage
 * - Budget syncs across devices (sync_scope: 'full')
 * - Falls back to in-memory storage if no backend provided
 *
 * @see docs/architecture/extracts/llm-cost-6.10.md
 */
import { NAMESPACES } from '@ownyou/shared-types';
import { MODEL_TIERS } from '../providers/registry';
import { DEFAULT_BUDGET_CONFIG } from './types';
const BUDGET_KEY = 'current_period';
export class DailyBudgetExceededError extends Error {
    constructor(message) {
        super(message);
        this.name = 'DailyBudgetExceededError';
    }
}
/**
 * BudgetManager - Tracks and enforces LLM budget
 */
export class BudgetManager {
    constructor(config) {
        this.usage = new Map();
        this.loadPromises = new Map();
        const { store, ...budgetConfig } = config ?? {};
        this.config = { ...DEFAULT_BUDGET_CONFIG, ...budgetConfig };
        this.store = store;
    }
    /**
     * Track usage for a user
     */
    async trackUsage(userId, record) {
        let summary = await this.loadUsage(userId);
        summary = this.rollDailyWindowIfNeeded(summary);
        // Update totals
        summary.totalCostUsd += record.costUsd;
        summary.dailyCostUsd += record.costUsd;
        summary.totalInputTokens += record.inputTokens;
        summary.totalOutputTokens += record.outputTokens;
        // Update operation counts
        const op = record.operation;
        summary.operationCounts[op] = (summary.operationCounts[op] ?? 0) + 1;
        // Update tokens by model
        if (!summary.tokensByModel[record.model]) {
            summary.tokensByModel[record.model] = { input: 0, output: 0 };
        }
        summary.tokensByModel[record.model].input += record.inputTokens;
        summary.tokensByModel[record.model].output += record.outputTokens;
        // Update in-memory cache
        this.usage.set(userId, summary);
        // Persist if store available
        await this.saveUsage(userId, summary);
    }
    /**
     * Load usage from store (with caching)
     */
    async loadUsage(userId) {
        // Check in-memory cache first
        const cached = this.usage.get(userId);
        if (cached) {
            // Check if period has expired
            if (cached.periodEnd > Date.now()) {
                return cached;
            }
            // Period expired, create new one
        }
        // Prevent concurrent loads for same user
        const existingPromise = this.loadPromises.get(userId);
        if (existingPromise) {
            return existingPromise;
        }
        const loadPromise = this.doLoadUsage(userId);
        this.loadPromises.set(userId, loadPromise);
        try {
            const summary = await loadPromise;
            this.usage.set(userId, summary);
            return summary;
        }
        finally {
            this.loadPromises.delete(userId);
        }
    }
    /**
     * Actual load from store
     */
    async doLoadUsage(userId) {
        if (this.store) {
            const stored = await this.store.get(NAMESPACES.LLM_BUDGET, userId, BUDGET_KEY);
            if (stored && stored.periodEnd > Date.now()) {
                return this.normalizeSummary(stored);
            }
        }
        // No stored data or expired, create new
        return this.createEmptySummary();
    }
    /**
     * Save usage to store
     */
    async saveUsage(userId, summary) {
        if (this.store) {
            await this.store.put(NAMESPACES.LLM_BUDGET, userId, BUDGET_KEY, summary);
        }
    }
    /**
     * Get current usage summary
     */
    async getCurrentUsage(userId) {
        const summary = this.rollDailyWindowIfNeeded(await this.loadUsage(userId));
        this.usage.set(userId, summary);
        await this.saveUsage(userId, summary);
        return summary;
    }
    /**
     * Get budget percentage used
     */
    async getBudgetPercentage(userId) {
        const summary = await this.getCurrentUsage(userId);
        return (summary.totalCostUsd / this.config.monthlyBudgetUsd) * 100;
    }
    /**
     * Throws before a paid provider call if today's known spend plus the estimated
     * request cost would exceed the hard daily cap.
     */
    async assertDailyBudgetAvailable(userId, estimatedCostUsd = 0) {
        const dailyBudget = this.config.dailyBudgetUsd;
        if (dailyBudget === undefined || dailyBudget <= 0)
            return;
        const summary = await this.getCurrentUsage(userId);
        const reserve = Math.max(0, estimatedCostUsd);
        if (summary.dailyCostUsd + reserve > dailyBudget) {
            throw new DailyBudgetExceededError(`Daily LLM budget exceeded: $${summary.dailyCostUsd.toFixed(2)} spent, ` +
                `$${reserve.toFixed(2)} requested, $${dailyBudget.toFixed(2)} cap.`);
        }
    }
    getUnknownRequestReserveUsd() {
        return Math.max(0, this.config.unknownRequestReserveUsd ?? 0);
    }
    /**
     * Get throttle decision based on current usage
     */
    async getThrottleDecision(userId) {
        const percent = await this.getBudgetPercentage(userId);
        const { throttling } = this.config;
        let action;
        let message;
        if (percent >= throttling.blockAt) {
            action = 'block';
            message = 'Budget exceeded. Requests blocked.';
        }
        else if (percent >= throttling.deferAt) {
            action = 'defer';
            message = 'Budget nearly exhausted. Non-urgent requests deferred.';
        }
        else if (percent >= throttling.downgradeAt) {
            action = 'downgrade';
            message = 'Budget high. Downgrading to cheaper models.';
        }
        else if (percent >= throttling.warnAt) {
            action = 'warn';
            message = 'Budget at 50%. Consider reducing usage.';
        }
        else {
            action = 'ok';
        }
        return { action, budgetPercent: percent, message };
    }
    /**
     * Select model based on tier and budget
     */
    selectModel(requestedTier, budgetPercent) {
        const { throttling } = this.config;
        // Over 100% - local only
        if (budgetPercent >= throttling.blockAt) {
            return 'local';
        }
        // Over 80% - downgrade
        if (budgetPercent >= throttling.downgradeAt) {
            const downgradedTier = this.downgradeModelTier(requestedTier);
            return MODEL_TIERS[downgradedTier].models[0];
        }
        // Under 80% - use requested tier
        return MODEL_TIERS[requestedTier].models[0];
    }
    /**
     * Downgrade model tier
     */
    downgradeModelTier(tier) {
        switch (tier) {
            case 'quality':
                return 'standard';
            case 'standard':
                return 'fast';
            case 'fast':
                return 'fast';
            case 'local':
                return 'local';
        }
    }
    /**
     * Reset monthly usage
     */
    async resetMonthlyUsage(userId) {
        const summary = this.createEmptySummary();
        this.usage.set(userId, summary);
        await this.saveUsage(userId, summary);
    }
    /**
     * Set usage for testing
     */
    async setUsageForTesting(userId, costUsd) {
        const summary = this.createEmptySummary();
        summary.totalCostUsd = costUsd;
        this.usage.set(userId, summary);
    }
    async setDailyUsageForTesting(userId, costUsd) {
        const summary = this.createEmptySummary();
        summary.dailyCostUsd = costUsd;
        this.usage.set(userId, summary);
    }
    /**
     * Create empty usage summary
     */
    createEmptySummary() {
        const now = Date.now();
        const { start, end } = this.getDailyWindow(now);
        return {
            totalCostUsd: 0,
            dailyCostUsd: 0,
            totalInputTokens: 0,
            totalOutputTokens: 0,
            operationCounts: {},
            tokensByModel: {},
            dailyPeriodStart: start,
            dailyPeriodEnd: end,
            periodStart: now,
            periodEnd: now + 30 * 24 * 60 * 60 * 1000, // 30 days
        };
    }
    normalizeSummary(summary) {
        const { start, end } = this.getDailyWindow(Date.now());
        return {
            ...summary,
            dailyCostUsd: typeof summary.dailyCostUsd === 'number' ? summary.dailyCostUsd : 0,
            dailyPeriodStart: typeof summary.dailyPeriodStart === 'number' ? summary.dailyPeriodStart : start,
            dailyPeriodEnd: typeof summary.dailyPeriodEnd === 'number' ? summary.dailyPeriodEnd : end,
        };
    }
    rollDailyWindowIfNeeded(summary) {
        const normalized = this.normalizeSummary(summary);
        if (normalized.dailyPeriodEnd > Date.now())
            return normalized;
        const { start, end } = this.getDailyWindow(Date.now());
        return {
            ...normalized,
            dailyCostUsd: 0,
            dailyPeriodStart: start,
            dailyPeriodEnd: end,
        };
    }
    getDailyWindow(now) {
        const date = new Date(now);
        const start = Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
        return { start, end: start + 24 * 60 * 60 * 1000 };
    }
}
